import { ScaleBand, ScaleLinear } from "d3";
import { useState } from "react";

import { Bar } from "@/charts/chartBehavior/bar";
import { Label } from "@/charts/chartBehavior/label";
import { Overlay } from "@/charts/chartBehavior/overlay";
import { Tooltip } from "@/charts/chartBehavior/tooltip";

interface BarData {
  name: string | number;
  value: number | string;
}

interface BarsProps {
  data: BarData[];
  xScale: ScaleBand<string>;
  yScale: ScaleLinear<number, number>;
  boundsHeight: number;
}

export const Bars = ({ data, xScale, yScale, boundsHeight }: BarsProps) => {
  const [hovered, setHovered] = useState<string | null>(null);

  const BAR_WIDTH_RATIO = 0.35;
  const OVERLAY_WIDTH_RATIO = 0.9;

  return (
    <g>
      {data.map((d, i) => {
        const nameStr = String(d.name);
        const xBand = xScale(nameStr);
        const y = yScale(typeof d.value === "number" ? d.value : Number(d.value) || 0);
        if (xBand === undefined) return null;

        const bandWidth = xScale.bandwidth();
        const barWidth = bandWidth * BAR_WIDTH_RATIO;
        const overlayWidth = bandWidth * OVERLAY_WIDTH_RATIO;
        const centerX = xBand + bandWidth / 2;
        const barHeight = boundsHeight - y;

        return (
          <g key={i}>
            {hovered === nameStr && (
              <Overlay
                centerX={centerX}
                boundsHeight={boundsHeight}
                width={overlayWidth}
              />
            )}

            <Bar centerX={centerX} y={y} barHeight={barHeight} width={barWidth} />

            {hovered === nameStr && (
              <Tooltip centerX={centerX} y={y} value={d.value} barWidth={barWidth} />
            )}

            <Label centerX={centerX} boundsHeight={boundsHeight} name={nameStr} />

            <rect
              data-testid="bar"
              x={xBand}
              y={0}
              width={bandWidth}
              height={boundsHeight + 50}
              fill="transparent"
              style={{ pointerEvents: "all" }}
              onMouseEnter={() => setHovered(nameStr)}
              onMouseLeave={() => setHovered(null)}
            />
          </g>
        );
      })}
    </g>
  );
};
